import type { IMessage, StompConfig } from '@stomp/stompjs'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import type {
  CoreNotifyEvent,
  HeartbeatMessage,
  JsRequest,
  TaskResult,
  WebSocketConfig
} from './types'
import { WebSocketStatus } from './types'
import UniData from '@/common/classes/UniData.ts'
import { createSafeClientData } from '@/services/core/core.service.ts'
import { useSignModal } from '@/stores/useSignModal.ts';
import { useAppStore } from '@/stores/app'
import apiService from '@/services/api.service'
import { useUserStore } from '@/stores/user'
import { handleSessionExpired, hasFullSession } from '@/common/plugins/axios'
import {
  isWsJsRequestDebugEnabled,
  logWsJsRequestInbound,
  logWsJsResponseOutbound,
} from '@/common/utils/wsJsRequestDebugLog'
import { createJsWebExceptionResponse } from '@/common/utils/jsWebException'
import { isForcedLogoutEventType } from '@/services/auth/forcedLogout.service'
import { IKeys } from '@/common/constants/ikeys'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'
import { onPlatformSessionIdsChanged } from '@/services/form-events/platformSessionSync'
import { reportError } from '@/common/utils/errorReporter'

const CHUNK_SIZE = 8192 // 8KB chunks

/** Активные platform sessionId (coreId) для STOMP `/client/event/{app|form}/{id}`. */
function collectActivePlatformCoreIds(appStore: ReturnType<typeof useAppStore>): Set<string> {
  const activeCoreIds = new Set<string>()
  const addEntry = (entry: unknown) => {
    const sid = readPlatformSessionIdFromPageEntry(entry)
    if (sid) activeCoreIds.add(sid)
  }
  Object.values(appStore.pagesData || {}).forEach(addEntry)
  Object.values(appStore.dialogs || {}).forEach(addEntry)
  Object.values(appStore.overlays || {}).forEach(addEntry)
  const current = normalizePlatformSessionId(appStore.currentSessionId)
  if (current) activeCoreIds.add(current)
  return activeCoreIds
}

/**
 * Detect Firefox for SockJS transport workaround.
 * Firefox can hang on WebSocket upgrade
 */
function isFirefox(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Firefox\//i.test(navigator.userAgent)
}

/** SockJS transports: prefer XHR for Firefox to avoid WebSocket handshake hang. */
function getSockJSTransports(): ('websocket' | 'xhr-streaming' | 'xhr-polling')[] {
  if (isFirefox()) {
    return ['xhr-streaming', 'xhr-polling']
  }
  return ['websocket', 'xhr-streaming', 'xhr-polling']
}

interface ChunkMessage {
  chunkId: string
  chunkIndex: number
  totalChunks: number
  data: string
  type: 'js-response'
}

interface ChunkedData {
  chunks: Map<number, string>
  totalChunks: number
  receivedChunks: number
}

interface PendingCustomScript {
  resolve: (value: any) => void
  reject: (error: any) => void
  timeoutId: ReturnType<typeof setTimeout> | null
}

export class WebSocketService {
  private client: Client | null = null
  private config: WebSocketConfig
  private statusCallbacks: Set<(status: WebSocketStatus) => void> = new Set()
  private eventCallbacks: Set<(event: CoreNotifyEvent) => void> = new Set()
  private jsRequestCallbacks: Set<(request: JsRequest) => Promise<any>> = new Set()
  /** События office-editor (callback DS) — `/client/event/office`. */
  private officeEventCallbacks: Set<(request: JsRequest) => void> = new Set()
  /** Soft form-events — `/client/event/form/{coreId}` (`_changed`, `_custom`, …). */
  private formEventCallbacks: Set<(event: CoreNotifyEvent) => void> = new Set()
  private heartbeatInterval: number | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private currentSocketId: string | null = null
  private authToken: string | undefined = undefined
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private chunkedMessages = new Map<string, ChunkedData>()
  private pendingCustomScript: PendingCustomScript | null = null
  private jsRequestSubscriptions = new Map<string, any>()
  private isRefreshingSession = false
  private eventSubscriptions: Array<{ unsubscribe: () => void }> = []
  private appEventSubscriptions = new Map<string, { unsubscribe: () => void }>()
  private formEventSubscriptions = new Map<string, { unsubscribe: () => void }>()
  private connectPromise: Promise<void> | null = null

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectDelay: 5000,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
      debug: import.meta.env.DEV,
      ...config
    }
  }

  /**
   * Устанавливает соединение STOMP-over-SockJS и настраивает подписки/heartbeat.
   *
   * - **Идемпотентно**: если уже подключено — промис сразу резолвится.
   * - **Авторизация**: сохраняет токен и отправляет его в заголовке `X-AUTH-TOKEN`.
   * - **Сайд-эффекты**: при подключении запускает heartbeat и подписывается на топики сервера.
   */
  connect(token?: string): Promise<void> {
    if (this.client?.connected) {
      return Promise.resolve()
    }

    if (token) {
      this.authToken = token
    }

    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.notifyStatus(WebSocketStatus.CONNECTING)

      const stompConfig: StompConfig = {
        webSocketFactory: () => {
          return new SockJS(this.config.endpoint, null, {
            transports: getSockJSTransports()
          })
        },

        connectHeaders: (() => {
          const authToken = token || this.authToken
          if (authToken) {
            return {
              'X-AUTH-TOKEN': authToken
            }
          }
          return undefined
        })(),

        heartbeatIncoming: this.config.heartbeatIncoming,
        heartbeatOutgoing: this.config.heartbeatOutgoing,

        reconnectDelay: 0,

        onConnect: (frame) => {
          const stompSessionId = frame.headers?.['session'] ?? null
          this.currentSocketId = stompSessionId
          console.log('[WebSocket] connected: sessionId from CONNECTED frame =', stompSessionId ?? '(null, will use from server queue)')

          this.reconnectAttempts = 0
          this.notifyStatus(WebSocketStatus.CONNECTED)
          this.subscribeToEvents()
          this.startHeartbeat()
          resolve()
        },

        onDisconnect: () => {
          this.rejectPendingCustomScript(new Error('WebSocket disconnected'))
          this.notifyStatus(WebSocketStatus.DISCONNECTED)
          this.stopHeartbeat()
          this.handleReconnect()
        },

        onStompError: async (frame) => {
          const errorMessage = frame.headers['message'] || 'WebSocket connection error'
          const isRememberMeError = errorMessage.includes('remember-me') ||
            errorMessage.includes('full session') ||
            errorMessage.includes('WebSocket unavailable')

          if (isRememberMeError) {
            // Remember-me authentication error - try to refresh session
            const hasAuthToken = document.cookie.includes('X-AUTH-TOKEN=')
            if (hasAuthToken) {
              // Refresh session asynchronously (don't wait for it to complete)
              this.refreshSession().catch(() => {
                // Session refresh failed, will be handled by reconnection logic
              })
            }
            // Reject the promise - reconnection logic will handle retry after session refresh
            this.notifyStatus(WebSocketStatus.DISCONNECTED)
            reject(new Error(errorMessage))
          } else {
            console.error('WebSocket STOMP error:', frame)
            this.notifyStatus(WebSocketStatus.ERROR)
            reject(new Error(errorMessage))
          }
        },

        onWebSocketError: async (event) => {
          // Check if error is related to remember-me authentication
          const errorMsg = (event as any)?.message || (event as any)?.error?.message || ''
          const isRememberMeError = errorMsg.includes('remember-me') ||
            errorMsg.includes('full session') ||
            errorMsg.includes('WebSocket unavailable')

          if (isRememberMeError) {
            // Try to refresh session if we have remember-me token
            const hasAuthToken = document.cookie.includes('X-AUTH-TOKEN=')
            if (hasAuthToken) {
              await this.refreshSession()
              // Will retry via handleReconnect
            }
            this.notifyStatus(WebSocketStatus.DISCONNECTED)
          } else {
            console.error('WebSocket error:', event)
            this.notifyStatus(WebSocketStatus.ERROR)
          }
        },

        onWebSocketClose: async (event) => {
          this.rejectPendingCustomScript(new Error('WebSocket connection closed'))
          this.handleReconnect()
        }
      }

      this.client = new Client(stompConfig)
      this.client.activate()
    }).finally(() => {
      this.connectPromise = null
    })

    return this.connectPromise as Promise<void>
  }

  /**
   * Корректно отключается и чистит таймеры/подписки.
   *
   * - Останавливает heartbeat и таймер реконнекта
   * - Отписывается от per-core подписок
   * - Реджектит незавершённый `executeCustomScript` (если он был)
   * - Сбрасывает внутреннее состояние socket/session
   */
  disconnect(): void {
    this.stopHeartbeat()

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    for (const [coreId, subscription] of this.jsRequestSubscriptions.entries()) {
      subscription.unsubscribe()
    }
    this.jsRequestSubscriptions.clear()

    if (this.client) {
      this.client.deactivate()
      this.client = null
    }
    this.currentSocketId = null
    this.reconnectAttempts = 0
    this.rejectPendingCustomScript(new Error('WebSocket disconnected'))
    this.notifyStatus(WebSocketStatus.DISCONNECTED)
  }

  /** Подключен ли STOMP-клиент */
  isConnected(): boolean {
    return this.client?.connected ?? false
  }

  /**
   * TODO:проверить обновление после выполнения action
   *
   * Выбирает STOMP session ID из заголовка и обновляет currentSocketId
   */
  private extractSessionIdFromMessage(message: IMessage): void {
    if (message.headers && message.headers['session']) {
      const realSessionId = message.headers['session']
      if (this.currentSocketId !== realSessionId) {
        this.currentSocketId = realSessionId
      }
    }
  }

  /**
   * Настраивает все STOMP-подписки после подключения: топик событий, чанки,
   * результат custom script, системные и app-события, подписки на js-request.
   * При повторном вызове (реконнект) сначала отписывается от старых подписок.
   */
  private subscribeToEvents(): void {
    if (!this.client) return

    if (this.eventSubscriptions.length > 0) {
      this.eventSubscriptions.forEach((subscription) => {
        try {
          subscription.unsubscribe()
        } catch (error) {
          // Ignore unsubscribe failures on reconnect
        }
      })
      this.eventSubscriptions = []
    }

    this.eventSubscriptions.push(this.client.subscribe('/client/task/session-id', (message: IMessage) => {
      try {
        const payload = JSON.parse(message.body) as { sessionId?: string }
        if (payload?.sessionId) {
          this.currentSocketId = payload.sessionId
          console.log('[WebSocket] sessionId received from server:', payload.sessionId)
        }
      } catch (e) {
        console.error('[WebSocket] failed to parse sessionId message:', message.body, e)
      }
    }))

    this.client.publish({
      destination: '/middle/session-id',
      body: JSON.stringify({})
    })
    console.log('[WebSocket] requested sessionId from server after subscribe')

    // Subscribe app / form events for current user/core
    this.updateAppEventSubscriptions()
    this.updateFormEventSubscriptions()

    // Legacy topic events (if server still uses /topic/event)
    this.eventSubscriptions.push(this.client.subscribe('/client/topic/event', (message: IMessage) => {
      try {
        // Extract session ID from message if available
        this.extractSessionIdFromMessage(message)

        const event: CoreNotifyEvent = JSON.parse(message.body)
        this.logMiddlewarePush('/client/topic/event', message.body, {
          eventType: event?.type,
          eventData: event?.data as Record<string, unknown> | undefined,
          eventPayload: event,
        })
        this.eventCallbacks.forEach(callback => callback(event))
      } catch (error) {
        console.error('Error parsing event:', error)
      }
    }))

    // Subscribe to chunks
    this.eventSubscriptions.push(this.client.subscribe('/client/task/chunk', (message: IMessage) => {
      try {
        // Extract session ID from message if available
        this.extractSessionIdFromMessage(message)

        const chunk: ChunkMessage = JSON.parse(message.body)
        this.handleIncomingChunk(chunk)
      } catch (error) {
        console.error('Error handling chunk:', error)
      }
    }))

    // Subscribe /client/task/js-custom-script-result
    this.eventSubscriptions.push(this.client.subscribe('/client/task/js-custom-script-result', (message: IMessage) => {
      const payload: any = message.body
      if (payload) {
        try {
          const request: JsRequest = JSON.parse(payload)
          console.log('Received Test request:', request)

          if (request.socketId && this.currentSocketId !== request.socketId) {
            this.currentSocketId = request.socketId
          }

          // TODO: получаем тело файла (ов) для подписания либо их хеши, отдаем в компонент modalStore-Certificates
          const modalStore = useSignModal();
          modalStore.openModal(
            '',
          );

        } catch (error) {
          // Return raw payload if JSON parsing fails
          console.error('Error handling JS request:', error)
        }
      }
      this.resolvePendingCustomScript(payload)
    }))

    this.eventSubscriptions.push(this.client.subscribe('/client/event/system', (message: IMessage) => {
      try {
        const eventPayload = JSON.parse(message.body)
        const eventData = eventPayload?.eventData?.fields?.fields

        let event: any = {}
        if (eventData?._updateCounters) {
          event = {
            data: eventData._updateCounters?.value,
            type: 'updateCounters',
          }
          this.logMiddlewarePush('/client/event/system', message.body, {
            eventType: 'updateCounters',
            eventPayload,
            eventData,
          })
        } else {
          const eventType = eventData._event?.value ?? eventData._event
          event = {
            data: eventData,
            type: eventType,
          }
          this.logMiddlewarePush('/client/event/system', message.body, {
            eventType,
            eventPayload,
            eventData,
          })
          if (eventType === ':activate') {
            void this.handleMenuActivateEvent(eventData)
          } else if (isForcedLogoutEventType(eventType)) {
            void this.handleForcedLogoutEvent(eventData, String(eventType))
          } else if (eventType === 'disconnected' || eventType === 'reconnected') {
            void this.handlePlatformConnectionEvent(String(eventType), eventPayload)
          } else if (
            eventType === IKeys.$USER_STATE ||
            eventType === IKeys.$TOGGLE_MENU_NOTIFICATIONS ||
            eventType === IKeys.$REQUEST_FOCUS
          ) {
            void this.handleMenuSystemEvent(String(eventType), eventData)
          }
        }

        this.eventCallbacks.forEach(callback => callback(event))
      } catch (error) {
        console.error('[WebSocket] Error parsing system event:', error)
      }
    }))

    // Office editor events (Document Server callback status)
    this.eventSubscriptions.push(this.client.subscribe('/client/event/office', (message: IMessage) => {
      try {
        const raw = JSON.parse(message.body) as JsRequest
        const normalized: JsRequest = {
          ...raw,
          className: raw.className ?? raw.class_name,
          methodName: raw.methodName ?? raw.method_name,
          objectId: raw.objectId ?? raw.object_id,
          args: raw.args || [],
          socketId: raw.socketId ?? raw.socket_id,
        }
        this.officeEventCallbacks.forEach(cb => cb(normalized))
      } catch (error) {
        console.error('[WebSocket] Error parsing office event:', error)
      }
    }))

    this.updateJsRequestSubscriptions()
  }

  /**
   * Поддерживает подписки на app-события по активным сессиям.
   * Список активных sessionId берётся из appStore.pagesData, dialogs, overlays и текущей session.
   */
  private updateAppEventSubscriptions(): void {
    if (!this.client?.connected) {
      return
    }

    const appStore = useAppStore()
    const activeCoreIds = collectActivePlatformCoreIds(appStore)

    for (const [coreId, subscription] of this.appEventSubscriptions.entries()) {
      if (!activeCoreIds.has(coreId)) {
        try {
          subscription.unsubscribe()
        } catch (error) {
          // Ignore unsubscribe failures
        }
        this.appEventSubscriptions.delete(coreId)
      }
    }

    for (const coreId of activeCoreIds) {
      if (this.appEventSubscriptions.has(coreId)) {
        continue
      }
      const destination = `/client/event/app/${coreId}`
      const subscription = this.client.subscribe(destination, (message: IMessage) => {
        try {
          // Extract session ID from message if available
          this.extractSessionIdFromMessage(message)

          const eventPayload = JSON.parse(message.body)
          const eventData = eventPayload?.eventData?.fields?.fields
          if (!eventData) {
            return
          }

          const eventType = eventData._event?.value
          if (!eventType) {
            return
          }

          const payloadSessionId = eventPayload?.sessionId ?? eventData?._sessionId?.value
          const destinationCoreId = String(coreId)
          if (payloadSessionId != null) {
            if (String(payloadSessionId) !== destinationCoreId) {
              return
            }
          }

          const event: CoreNotifyEvent = {
            type: eventType,
            data: eventData,
            coreId: destinationCoreId,
          }
          this.logMiddlewarePush(destination, message.body, {
            eventType,
            eventPayload,
            eventData,
            coreId: destinationCoreId,
          })
          this.eventCallbacks.forEach(callback => callback(event))
        } catch (error) {
          console.error('Error parsing app event:', error)
        }
      })
      this.appEventSubscriptions.set(coreId, subscription)
    }
  }

  /**
   * Подписки на form soft-events по активным сессиям (`/client/event/form/{coreId}`).
   * Список coreId — тот же, что для app-событий.
   */
  private updateFormEventSubscriptions(): void {
    if (!this.client?.connected) {
      return
    }

    const appStore = useAppStore()
    const activeCoreIds = collectActivePlatformCoreIds(appStore)

    for (const [coreId, subscription] of this.formEventSubscriptions.entries()) {
      if (!activeCoreIds.has(coreId)) {
        try {
          subscription.unsubscribe()
        } catch (error) {
          // Ignore unsubscribe failures
        }
        this.formEventSubscriptions.delete(coreId)
      }
    }

    for (const coreId of activeCoreIds) {
      if (this.formEventSubscriptions.has(coreId)) {
        continue
      }
      const destination = `/client/event/form/${coreId}`
      const subscription = this.client.subscribe(destination, (message: IMessage) => {
        try {
          this.extractSessionIdFromMessage(message)

          const eventPayload = JSON.parse(message.body)
          const eventData = eventPayload?.eventData?.fields?.fields
          if (!eventData) {
            return
          }

          const eventType = eventData._event?.value ?? eventData._event
          if (!eventType) {
            return
          }

          const payloadSessionId = eventPayload?.sessionId ?? eventData?._sessionId?.value
          const destinationCoreId = String(coreId)
          if (payloadSessionId != null && String(payloadSessionId) !== '') {
            if (String(payloadSessionId) !== destinationCoreId) {
              return
            }
          }

          const event: CoreNotifyEvent = {
            type: String(eventType),
            data: eventData,
            coreId: destinationCoreId,
            timestamp: Date.now(),
          }
          this.logMiddlewarePush(destination, message.body, {
            eventType: event.type,
            eventPayload,
            eventData,
            coreId: destinationCoreId,
          })
          this.formEventCallbacks.forEach((callback) => callback(event))
        } catch (error) {
          console.error('Error parsing form event:', error)
        }
      })
      this.formEventSubscriptions.set(coreId, subscription)
    }
  }

  /**
   * Обновляет подписки на js-request по активным sessionId.
   */
  private updateJsRequestSubscriptions(): void {
    if (!this.client) {
      return
    }

    if (!this.client.connected) {
      return
    }

    const appStore = useAppStore()
    const activeCoreIds = collectActivePlatformCoreIds(appStore)

    for (const [coreId, subscription] of this.jsRequestSubscriptions.entries()) {
      if (!activeCoreIds.has(coreId)) {
        try {
          subscription.unsubscribe()
          this.jsRequestSubscriptions.delete(coreId)
        } catch (error) {
          console.error(`[WebSocket] Error unsubscribing from ${coreId}:`, error)
          // Continue even if unsubscribe fails
          this.jsRequestSubscriptions.delete(coreId)
        }
      }
    }

    for (const coreId of activeCoreIds) {
      if (!this.jsRequestSubscriptions.has(coreId)) {
        const subscription = this.client.subscribe(
          `/client/task/js-request/${coreId}`,
          async (message: IMessage) => {
            try {
              // Extract session ID from message if available
              this.extractSessionIdFromMessage(message)

              const request: JsRequest = JSON.parse(message.body)
              logWsJsRequestInbound(request, {
                coreSessionId: coreId,
                destination: `/client/task/js-request/${coreId}`,
                rawBodyLength: message.body?.length,
              })
              await this.handleJsRequest(request, message, coreId)
            } catch (error) {
              console.error('Error handling JS request:', error)
            }
          }
        )
        this.jsRequestSubscriptions.set(coreId, subscription)
      }
    }
  }

  /**
   * Отправляет результат выполнения JS-задачи на middle-слой.
   * Если соединение не установлено — ничего не делает.
   */
  sendTaskResult(result: TaskResult): void {
    if (!this.client?.connected) {
      console.warn('WebSocket not connected, cannot send task result')
      return
    }

    this.client.publish({
      destination: '/middle/task/js-response',
      body: JSON.stringify(result)
    })
  }

  /**
   * Отправляет сообщение на выполнение "custom script" (сейчас используется для тестового endpoint).
   * Если соединение не установлено — ничего не делает.
   */
  sendTestMessage(message: any): void {
    if (!this.client?.connected) {
      console.warn('WebSocket not connected, cannot send task message')
      return
    }
    this.client.publish({
      destination: '/middle/task/js-custom-script-execute',
      body: JSON.stringify(message)
    })
  }

  /**
   * Обрабатывает входящий JS-запрос.
   * Берёт первый зарегистрированный обработчик (через `onJsRequest`) и публикует его результат.
   */
  private async handleJsRequest(
    request: JsRequest,
    message: IMessage,
    coreSessionId?: string,
  ): Promise<void> {

    if (this.jsRequestCallbacks.size === 0) {
      console.warn('No JS request handlers registered')
      return
    }

    const socketId = request.socketId ?? request.socket_id
    if (!socketId || socketId.trim() === '') {
      console.warn('[WebSocket] JS request missing socketId, skipping response')
      return
    }
    const requestId = request.request_id ?? null

    try {
      const handler = Array.from(this.jsRequestCallbacks)[0]
      const requestWithArgs = {
        ...request,
        className: request.className ?? request.class_name,
        methodName: request.methodName ?? request.method_name,
        objectId: request.objectId ?? request.object_id,
        args: request.args || [],
        socketId,
        coreSessionId: coreSessionId ?? request.coreSessionId,
      }
      const result = await handler(requestWithArgs)
      this.sendJsResponse(result, socketId, requestId, {
        className: requestWithArgs.className,
        methodName: requestWithArgs.methodName,
      })
    } catch (error: any) {
      console.error('Error executing JS request:', error)
      reportError(error, {
        source: 'websocket.service',
        className: String(request.className ?? request.class_name ?? ''),
        methodName: String(request.methodName ?? request.method_name ?? ''),
      })
      this.sendJsResponse(createJsWebExceptionResponse(error), socketId, requestId, {
        className: request.className ?? request.class_name,
        methodName: request.methodName ?? request.method_name,
      })
    }
  }

  /**
   * Публикует ответ на JS-запрос в `/middle/task/js-response`.
   *
   * - Гарантирует наличие `__socketId` в payload (требование маршрутизации на сервере).
   * - Автоматически переключается на чанки, если payload больше `CHUNK_SIZE`.
   */
  private sendJsResponse(
    data: any,
    requestSocketId?: string,
    requestId?: string | null,
    logContext?: { className?: string; methodName?: string },
  ): void {
    if (!this.client?.connected) {
      console.warn('WebSocket not connected, cannot send JS response')
      return
    }

    if (!requestSocketId) {
      return
    }

    // Ensure socketId is always included in the response
    if (data && typeof data === 'object' && !data.error) {
      // Normal response - add socketId to fields structure
      if (data.fields?.fields) {
        data.fields.fields.__socketId = {
          type: 'String',
          value: requestSocketId
        }
      } else if (data.fields) {
        if (!data.fields.fields) {
          data.fields.fields = {}
        }
        data.fields.fields.__socketId = {
          type: 'String',
          value: requestSocketId
        }
      } else {
        data.fields = {
          fields: {
            __socketId: {
              type: 'String',
              value: requestSocketId
            }
          }
        }
      }
    } else if (data && typeof data === 'object' && data.error) {
      // Error response - wrap in proper structure with socketId
      data = {
        fields: {
          fields: {
            __socketId: {
              type: 'String',
              value: requestSocketId
            },
            error: {
              type: 'String',
              value: data.error
            }
          }
        }
      }
    }

    const finalBody = JSON.stringify(data)
    const responsePath = requestId ? `/${requestId}` : ''
    const chunked = finalBody.length > CHUNK_SIZE

    logWsJsResponseOutbound(data, {
      requestSocketId,
      requestId,
      chunked,
      bodyLength: finalBody.length,
      chunkCount: chunked ? Math.ceil(finalBody.length / CHUNK_SIZE) : undefined,
      className: logContext?.className,
      methodName: logContext?.methodName,
    })

    if (chunked) {
      this.sendInChunks(finalBody, 'js-response', requestId)
    } else {
      this.client.publish({
        destination: '/middle/task/js-response' + responsePath,
        body: finalBody
      })
    }
  }

  /**
   * Разбивает большой payload на части по `CHUNK_SIZE` и публикует их в `/middle/task/chunk`.
   * Используется, когда превышены ограничения на размер сообщения у клиента/сервера.
   */
  private sendInChunks(data: string, type: 'js-response', requestId?: string | null): void {
    const totalChunks = Math.ceil(data.length / CHUNK_SIZE)
    const chunkId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const responsePath = requestId ? `/${requestId}` : ''


    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, data.length)
      const chunk: ChunkMessage = {
        chunkId,
        chunkIndex: i,
        totalChunks,
        data: data.substring(start, end),
        type
      }

      this.client!.publish({
        destination: '/middle/task/chunk' + responsePath,
        body: JSON.stringify(chunk)
      })
    }
  }

  /**
   * Собирает входящие чанки в одно сообщение и (опционально) парсит итоговый JSON payload.
   * Сейчас реализация только валидирует JSON; это точка расширения для дальнейшей обработки.
   */
  private handleIncomingChunk(chunk: ChunkMessage): void {
    const { chunkId, chunkIndex, totalChunks, data } = chunk

    if (!this.chunkedMessages.has(chunkId)) {
      this.chunkedMessages.set(chunkId, {
        chunks: new Map(),
        totalChunks,
        receivedChunks: 0
      })
    }

    const message = this.chunkedMessages.get(chunkId)!
    message.chunks.set(chunkIndex, data)
    message.receivedChunks++

    if (message.receivedChunks === totalChunks) {
      const fullData = Array.from({ length: totalChunks }, (_, i) => message.chunks.get(i)).join('')
      this.chunkedMessages.delete(chunkId)

      try {
        const parsed = JSON.parse(fullData)
        // Process parsed data if needed
      } catch (error) {
        console.error('Error parsing reassembled chunks:', error)
      }
    }
  }

  /**
   * Возвращает текущий STOMP session id (из CONNECTED frame, из очереди session-id или из заголовков сообщений).
   */
  getSocketId(): string | null {
    return this.currentSocketId
  }

  /**
   * Ждёт появления session id (например, из очереди /client/queue/session-id) в течение timeoutMs.
   * Если id уже есть или соединения нет — резолвится сразу.
   * @param timeoutMs максимум мс ожидания
   * @returns текущий socketId или null по таймауту/отключению
   */
  waitForSocketId(timeoutMs: number): Promise<string | null> {
    if (this.currentSocketId) {
      return Promise.resolve(this.currentSocketId)
    }
    if (!this.client?.connected) {
      return Promise.resolve(null)
    }
    const start = Date.now()
    const pollMs = 50
    return new Promise((resolve) => {
      const t = setInterval(() => {
        if (this.currentSocketId) {
          clearInterval(t)
          resolve(this.currentSocketId)
          return
        }
        if (!this.client?.connected || Date.now() - start >= timeoutMs) {
          clearInterval(t)
          resolve(this.currentSocketId)
        }
      }, pollMs)
    })
  }

  /**
   * Запускает heartbeat-таймер:
   * - обновляет динамические подписки (per-core) пока есть соединение
   * - отправляет `/middle/ping` со списком активных coreId
   */
  private startHeartbeat(): void {
    this.stopHeartbeat()

    const appStore = useAppStore()

    this.heartbeatInterval = window.setInterval(() => {
      if (this.client?.connected) {
        // Update subscriptions when active cores change
        this.updateSubscriptions()

        const heartbeat: HeartbeatMessage = {
          timestamp: Date.now(),
          activeCores: Array.from(collectActivePlatformCoreIds(appStore)),
        }

        this.client.publish({
          destination: '/middle/ping',
          body: JSON.stringify(heartbeat)
        })
      }
    }, 30000)
  }

  /** Останавливает heartbeat-таймер, если он запущен. */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Обновляет сессию через API (используется для remember-me / восстановления full-session).
   * Гарантирует, что одновременно выполняется только одно обновление.
   */
  private async refreshSession(): Promise<boolean> {
    if (this.isRefreshingSession) {
      while (this.isRefreshingSession) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return hasFullSession()
    }

    this.isRefreshingSession = true
    try {
      const userStore = useUserStore()
      await userStore.verifySession()
      // Wait for cookies to be set
      await new Promise(resolve => setTimeout(resolve, 1000))
      return true
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return true
      }
      return false
    } finally {
      this.isRefreshingSession = false
    }
  }

  /**
   * Планирует реконнект с экспоненциальной задержкой (exponential backoff).
   * Не пытается реконнектиться, если нет аутентификации (нет full session и нет remember-me токена).
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket]: Max reconnect attempts reached')
      this.notifyStatus(WebSocketStatus.ERROR)
      return
    }

    // Check authentication state
    const hasSession = hasFullSession()
    const hasAuthToken = document.cookie.includes('X-AUTH-TOKEN=')

    // Don't try to reconnect if we don't have any authentication
    if (!hasSession && !hasAuthToken) {
      this.notifyStatus(WebSocketStatus.DISCONNECTED)
      return
    }

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    this.reconnectAttempts++
    this.notifyStatus(WebSocketStatus.RECONNECTING)

    // Use exponential backoff for reconnection attempts
    const baseDelay = this.config?.reconnectDelay ?? 5000
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts - 1), 30000)

    // Schedule reconnection with stored auth token
    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectTimeout = null

      await this.refreshSession()

      // Deactivate current client if it exists
      if (this.client) {
        this.client.deactivate()
        this.client = null
      }

      // Reconnect with stored auth token
      this.connect(this.authToken).catch((error) => {

        // Check if error is due to remember-me authentication
        const errorMessage = error?.message || ''
        const isRememberMeError = errorMessage.includes('remember-me') ||
          errorMessage.includes('full session') ||
          errorMessage.includes('WebSocket unavailable')

        if (isRememberMeError) {
          // Try to refresh session and retry once more
          this.refreshSession().then((sessionRefreshed) => {
            if (sessionRefreshed && this.reconnectAttempts < this.maxReconnectAttempts) {
              this.reconnectAttempts = Math.max(0, this.reconnectAttempts - 2)
              this.handleReconnect()
            } else {
              this.notifyStatus(WebSocketStatus.ERROR)
              // No full session after refresh — clear auth and redirect to login
              handleSessionExpired()
            }
          }).catch(() => {
            this.notifyStatus(WebSocketStatus.ERROR)
            handleSessionExpired()
          })
        } else if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.handleReconnect()
        } else {
          this.notifyStatus(WebSocketStatus.ERROR)
        }
      })
    }, delay)
  }

  /**
   * Запускает выполнение custom script на сервере и ждёт одно сообщение с результатом.
   *
   * - Отклоняет (reject), если нет соединения
   * - Отклоняет (reject), если уже выполняется другой запрос custom script
   * - Поддерживает таймаут (по умолчанию: 10с)
   */
  executeCustomScript(payload: any, timeoutMs = 300000): Promise<any> {
    if (!this.client?.connected) {
      return Promise.reject(new Error('WebSocket not connected. Cannot execute custom script.'))
    }

    if (this.pendingCustomScript) {
      return Promise.reject(new Error('Another custom script request is already in progress'))
    }

    return new Promise((resolve, reject) => {
      const timeoutId = timeoutMs > 0
        ? setTimeout(() => {
          this.rejectPendingCustomScript(new Error('Custom script execution timed out'))
        }, timeoutMs)
        : null

      this.pendingCustomScript = {
        resolve,
        reject,
        timeoutId
      }

      try {
        const body = typeof payload === 'string' ? payload : JSON.stringify(payload)
        this.client!.publish({
          destination: '/middle/task/js-custom-script-execute',
          body
        })
      } catch (error: any) {
        this.rejectPendingCustomScript(error instanceof Error ? error : new Error(String(error)))
      }
    })
  }

  /** Резолвит ожидающий промис `executeCustomScript` (если он есть). */
  private resolvePendingCustomScript(result: any): void {
    if (!this.pendingCustomScript) {
      return
    }

    const entry = this.pendingCustomScript
    this.pendingCustomScript = null

    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId)
    }

    entry.resolve(result)
  }

  /** Реджектит ожидающий промис `executeCustomScript` (если он есть). */
  private rejectPendingCustomScript(error: Error): void {
    if (!this.pendingCustomScript) {
      return
    }

    const entry = this.pendingCustomScript
    this.pendingCustomScript = null

    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId)
    }

    entry.reject(error)
  }

  /**
   * Лог PUSH-сообщений от middleware.
   * По умолчанию выключен (в т.ч. на проде). Включение: localStorage `debug_ws_push=1`.
   */
  private logMiddlewarePush(
    destination: string,
    rawBody: string,
    details?: {
      eventType?: unknown
      eventPayload?: unknown
      eventData?: Record<string, unknown> | undefined
      enrichedData?: Record<string, unknown> | undefined
      coreId?: string
    },
  ): void {
    let enabled = false
    try {
      enabled = typeof localStorage !== 'undefined' && localStorage.getItem('debug_ws_push') === '1'
    } catch {
      enabled = false
    }
    if (!enabled) return

    const eventType = details?.eventType != null ? String(details.eventType) : 'unknown'
    const eventData = details?.enrichedData ?? details?.eventData
    const readField = (key: string): unknown => {
      if (!eventData || typeof eventData !== 'object') return undefined
      const entry = (eventData as Record<string, unknown>)[key]
      if (entry && typeof entry === 'object' && entry !== null && 'value' in entry) {
        return (entry as { value: unknown }).value
      }
      return entry
    }

    const payloadSessionId =
      details?.eventPayload &&
      typeof details.eventPayload === 'object' &&
      details.eventPayload !== null &&
      'sessionId' in details.eventPayload
        ? (details.eventPayload as { sessionId?: unknown }).sessionId
        : undefined

    const summaryParts = [
      eventType !== 'unknown' ? `type=${eventType}` : null,
      readField('_listener') != null ? `listener=${readField('_listener')}` : null,
      readField('action') != null ? `action=${readField('action')}` : null,
      readField('_message.param._id') != null ? `paramId=${readField('_message.param._id')}` : null,
      readField('_message.param._typeId') != null ? `paramType=${readField('_message.param._typeId')}` : null,
      payloadSessionId != null && String(payloadSessionId).trim() !== ''
        ? `sessionId=${payloadSessionId}`
        : null,
      details?.coreId ? `coreId=${details.coreId}` : null,
    ].filter(Boolean)

    const messageText = readField('_message.message')
    console.groupCollapsed(
      `[WebSocket][PUSH] ${destination}${summaryParts.length ? ` — ${summaryParts.join(', ')}` : ''}`,
    )
    if (messageText != null && String(messageText).trim() !== '') {
      console.log('message:', messageText)
    }
    if (details?.eventPayload != null) {
      console.log('eventPayload:', details.eventPayload)
    }
    if (details?.enrichedData != null && details.enrichedData !== details?.eventData) {
      console.log('enriched eventData:', details.enrichedData)
    } else if (details?.eventData != null) {
      console.log('eventData:', details.eventData)
    }
    console.log('raw body:', rawBody)
    console.groupEnd()
  }

  /**
   * Подписывает на изменение статуса соединения.
   * Возвращает функцию отписки.
   */
  onStatusChange(callback: (status: WebSocketStatus) => void): () => void {
    this.statusCallbacks.add(callback)
    return () => this.statusCallbacks.delete(callback)
  }

  /**
   * Подписывает на события core notify / app / system.
   * Возвращает функцию отписки.
   */
  onEvent(callback: (event: CoreNotifyEvent) => void): () => void {
    this.eventCallbacks.add(callback)
    return () => this.eventCallbacks.delete(callback)
  }

  /**
   * Регистрирует обработчик входящих JS-запросов. Возвращает функцию отписки.
   * Используется первый зарегистрированный обработчик.
   */
  onJsRequest(callback: (request: JsRequest) => Promise<any>): () => void {
    this.jsRequestCallbacks.add(callback)
    return () => this.jsRequestCallbacks.delete(callback)
  }

  /**
   * Подписывает на события office-editor (callback DS):
 * `OfficeCallbackHandler.statusChanged`: object_id + args [status, documentKey, storageUri?].
   * Возвращает функцию отписки.
   */
  onOfficeEvent(callback: (request: JsRequest) => void): () => void {
    this.officeEventCallbacks.add(callback)
    return () => this.officeEventCallbacks.delete(callback)
  }

  /**
   * Подписывает на form soft-events (`/client/event/form/{coreId}`).
   * Возвращает функцию отписки.
   */
  onFormEvent(callback: (event: CoreNotifyEvent) => void): () => void {
    this.formEventCallbacks.add(callback)
    return () => this.formEventCallbacks.delete(callback)
  }

  // Обновление подписок (watcher при изменении formStackFids и currentFormFid)
  /**
   * Принудительно обновляет per-core подписки при активном соединении.
   * Можно вызывать часто; если соединения нет — ничего не делает.
   */
  updateSubscriptions(): void {
    if (this.client?.connected) {
      this.updateJsRequestSubscriptions()
      this.updateAppEventSubscriptions()
      this.updateFormEventSubscriptions()
    }
  }

  /** Уведомляет всех подписчиков статуса. */
  private notifyStatus(status: WebSocketStatus): void {
    this.statusCallbacks.forEach(callback => callback(status))
  }

  private async handleForcedLogoutEvent(
    eventData: Record<string, unknown>,
    eventType: string,
  ): Promise<void> {
    try {
      const { handleForcedLogoutSystemEvent } = await import('@/services/auth/forcedLogout.service')
      const { useNotification } = await import('@/common/composables/useNotification')
      const { useUserStore } = await import('@/stores/user')
      const router = (await import('@/router/index')).default
      const userStore = useUserStore()
      const notification = useNotification()

      await handleForcedLogoutSystemEvent(eventData, eventType, {
        userStore,
        wsService: this,
        router,
        notify: (toast) => {
          if (toast.severity === 'warn') {
            notification.warn(toast)
          } else {
            notification.info(toast)
          }
        },
      })
    } catch (error) {
      console.error('[WebSocket] Forced logout handler failed:', error)
    }
  }

  private async handlePlatformConnectionEvent(
    eventType: string,
    eventPayload: unknown,
  ): Promise<void> {
    try {
      const { handlePlatformConnectionSystemEvent, readCoreNotifySessionId } = await import(
        '@/services/form-events/platformConnection.service'
      )
      await handlePlatformConnectionSystemEvent(eventType, {
        sessionId: readCoreNotifySessionId(eventPayload),
      })
    } catch (error) {
      console.error('[WebSocket] Platform connection handler failed:', error)
    }
  }

  private async handleMenuSystemEvent(
    eventType: string,
    eventData: Record<string, unknown>,
  ): Promise<void> {
    try {
      const { handleMenuSystemEvent } = await import('@/services/menu/menuSystemEvents.service')
      handleMenuSystemEvent(eventType, eventData)
    } catch (error) {
      console.error('[WebSocket] Menu system event handler failed:', error)
    }
  }

  private async handleMenuActivateEvent(eventData: Record<string, unknown>): Promise<void> {
    try {
      const { handleMenuActivateSystemEvent } = await import('@/services/menu/menuActivation.service')
      const { useDocumentTabsStore } = await import('@/stores/documentTabs')
      const router = (await import('@/router/index')).default
      const appStore = useAppStore()
      const documentTabsStore = useDocumentTabsStore()
      await handleMenuActivateSystemEvent(eventData, {
        appStore,
        documentTabsStore,
        router,
        sideMenu: appStore.sideMenu,
        rawMenu: appStore.rawMenu,
      })
    } catch (error) {
      console.error('[WebSocket] Menu :activate handler failed:', error)
    }
  }
}

let webSocketServiceInstance: WebSocketService | null = null

export function getWebSocketService(): WebSocketService {
  if (!webSocketServiceInstance) {
    let endpoint: string
    if (import.meta.env.DEV) {
      // In dev mode, use the proxy path so CORS is handled correctly
      // SockJS will use HTTP polling through the proxy, which works fine
      endpoint = import.meta.env.VITE_API_WS_URL || `${window.location.origin}/api/ws-endpoint`
    } else {
      endpoint = import.meta.env.VITE_API_WS_URL ||
        (() => {
          const baseURL = import.meta.env.VITE_API_BASE_URL || window.location.origin
          const cleanBaseURL = baseURL.endsWith('/api') ? baseURL.slice(0, -4) : baseURL
          return `${cleanBaseURL}/ws-endpoint`
        })()
    }

    webSocketServiceInstance = new WebSocketService({
      endpoint,
      debug: import.meta.env.DEV
    })

    onPlatformSessionIdsChanged(() => {
      webSocketServiceInstance?.updateSubscriptions()
    })

    // Expose to window for debugging (dev mode only)
    if (import.meta.env.DEV) {
      ;(window as any).__wsService = webSocketServiceInstance
      ;(window as any).__wsJsRequestDebug = {
        enabled: isWsJsRequestDebugEnabled,
        enable: () => localStorage.setItem('debug_ws_js_request', 'true'),
        disable: () => localStorage.removeItem('debug_ws_js_request'),
      }
    }
  }
  return webSocketServiceInstance
}

/**
 * Simulate WebSocket disconnect for testing reconnection
 * Usage in browser console: simulateWsDisconnect()
 */
export function simulateWsDisconnect(): void {
  if (!webSocketServiceInstance) {
    console.warn('WebSocket service not initialized')
    return
  }

  // Force close the underlying connection to trigger reconnection
  const client = (webSocketServiceInstance as any).client
  if (client?.webSocket) {
    client.webSocket.close()
  } else if (client) {
    client.deactivate()
  }
}

// Expose simulate function to window in dev mode
if (import.meta.env.DEV) {
  (window as any).simulateWsDisconnect = simulateWsDisconnect
}

export default getWebSocketService()

